import React, { useState, useEffect } from 'react';
import { Switch, Route, Redirect, useParams } from 'react-router-dom';

import { noop } from 'common/utils';
import { Routes } from 'routes';
import { PersonalSettingsContent } from './PersonalSettings/PersonalSettingsContent';
import { SecuritySettingsContent } from './SecuritySettingsContent';
import { PaymentSettingsContent } from 'features/account-settings/components/SettingsTab/PaymentOptions/PaymentSettingsContent';
import NotificationsContent from './Notifications';
import InviteFriendsFamilyContent from './InviteFriendsFamily';
import useFeatureFlags, { FeatureFlagTypes } from 'common/hooks/useFeatureFlags';

const Container = 'div';

interface SettingsContextProps {
    activeMenuItem: string;
    setActiveMenuItem: (_: string) => void;
}

const initialState = {
    activeMenuItem: 'personal',
    setActiveMenuItem: noop,
};

export const SettingsContext = React.createContext<SettingsContextProps>(initialState);

export const SettingsView = ({ children }: React.PropsWithChildren) => {
    const [activeMenuItem, setActiveMenuItem] = useState('personal-information');
    const { tab: activeTab } = useParams<{ tab: string }>();
    const { isEnabled, loadingFlags } = useFeatureFlags();

    useEffect(() => {
        if (activeTab) {
            setActiveMenuItem(activeTab);
        } else {
            setActiveMenuItem('personal-information');
        }
    }, [activeTab]);

    const context = {
        activeMenuItem,
        setActiveMenuItem,
    };

    return (
        <SettingsContext.Provider value={context}>
            <Container className="flex flex-col sm:flex-row">
                {children}
                <div className="w-full py-7 sm:px-24">
                    <Switch>
                        <Route
                            path={Routes.ShopAccountSettingsInviteFriendsFamilyOptionsTab}
                            component={InviteFriendsFamilyContent}
                        />
                        <Route
                            path={Routes.ShopAccountSettingsPersonalInformationTab}
                            component={PersonalSettingsContent}
                        />
                        <Route
                            path={Routes.ShopAccountSettingsSecurityTab}
                            component={SecuritySettingsContent}
                        />
                        <Route
                            path={Routes.ShopAccountSettingsPaymentOptionsTab}
                            component={PaymentSettingsContent}
                        />
                        {!loadingFlags && isEnabled(FeatureFlagTypes.enable_iterable) ? (
                            <Route
                                path={Routes.ShopAccountSettingsNotificationsTab}
                                component={NotificationsContent}
                            />
                        ) : null}
                        <Route path={Routes.ShopAccountSettingsTab}>
                            <Redirect to={Routes.ShopAccountSettingsPersonalInformationTab} />
                        </Route>
                    </Switch>
                </div>
            </Container>
        </SettingsContext.Provider>
    );
};

interface EditableFieldsContextProps {
    fieldToEdit: string;
    setFieldToEdit: (_: string) => void;
}

const initialPersonalSettingsState = {
    fieldToEdit: '',
    setFieldToEdit: noop,
};

export const EditableFieldsContext = React.createContext<EditableFieldsContextProps>(
    initialPersonalSettingsState
);

export const EditableFieldsView = ({ children }: React.PropsWithChildren) => {
    const [fieldToEdit, setFieldToEdit] = useState<string>('');
    const context = { fieldToEdit, setFieldToEdit };
    return (
        <EditableFieldsContext.Provider value={context}>{children}</EditableFieldsContext.Provider>
    );
};
